import { create } from "zustand";
import { devtools } from "zustand/middleware";
import * as CartApi from "@src/api/CartApi";
import { Cart } from "@src/types/cart";

type Address = {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  [key: string]: any;
};

type CartState = {
  cart: Cart | null;
  cartCount: number;
  loading: boolean;
  error: string | null;
  fetchCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  setCart: (cart: Cart) => void;
  fetchCartCount: () => Promise<void>;
  refreshCartCount: () => Promise<void>;
  setCartAddress: (address: Address) => Promise<void>;
};

export const useCartStore = create<CartState>()(
  devtools((set) => {
    const store: CartState = {
      cart: null,
      cartCount: 0,
      loading: false,
      error: null,

      fetchCart: async () => {
        set({ loading: true, error: null }, false, "cart/fetch-start");

        try {
          const response = await CartApi.getCartWithLivePricing();
          const cart = response.body;
          set(
            {
              cart,
              cartCount: cart?.itemCount ?? 0,
              loading: false,
            },
            false,
            "cart/fetch-success",
          );
        } catch (err: any) {
          set(
            {
              error: err.message || "Unknown error while fetching cart",
              loading: false,
            },
            false,
            "cart/fetch-error",
          );
        }
      },

      refreshCart: async () => {
        try {
          const response = await CartApi.getCartWithLivePricing();
          const cart = response.body;
          set(
            {
              cart,
              cartCount: cart?.itemCount ?? 0,
            },
            false,
            "cart/refresh",
          );
        } catch (err: any) {
          console.error("Failed to refresh cart:", err);
          set(
            {
              error: err.message || "Unknown error while refreshing cart",
            },
            false,
            "cart/refresh-error",
          );
        }
      },

      setCart: (cart: Cart) => {
        set(
          {
            cart,
            cartCount: cart?.itemCount ?? 0,
          },
          false,
          "cart/set",
        );
      },

      fetchCartCount: async () => {
        set({ loading: true, error: null }, false, "cartCount/fetch-start");
        try {
          const response = await CartApi.getCartCount();
          const itemCount = response.body.itemCount || 0;
          set(
            { cartCount: itemCount, loading: false },
            false,
            "cartCount/fetch-success",
          );
        } catch (err: any) {
          set(
            {
              error: err.message || "Unknown error while fetching cart count",
              loading: false,
            },
            false,
            "cartCount/fetch-error",
          );
        }
      },

      refreshCartCount: async () => {
        try {
          const response = await CartApi.getCartCount();
          const itemCount = response.body.itemCount || 0;
          set({ cartCount: itemCount }, false, "cartCount/refresh");
        } catch (err: any) {
          console.error("Failed to refresh cart count:", err);
          set(
            {
              error: err.message || "Unknown error while refreshing cart count",
            },
            false,
            "cartCount/refresh-error",
          );
        }
      },

      setCartAddress: async (address: Address) => {
        set({ loading: true, error: null }, false, "cartAddress/set-start");
        try {
          const response = await CartApi.setCartAddress(address);
          const nextCart = response.body;
          set(
            {
              cart: nextCart,
              cartCount: nextCart?.itemCount ?? 0,
              loading: false,
            },
            false,
            "cartAddress/set-success",
          );
        } catch (err: any) {
          set(
            {
              error: err.message || "Unknown error while setting cart address",
              loading: false,
            },
            false,
            "cartAddress/set-error",
          );
        }
      },
    };

    store.fetchCart();

    return store;
  }),
);
